import { CountryCode, parsePhoneNumber } from 'libphonenumber-js/mobile';

import { Pipe, PipeTransform } from '@angular/core';

import { LocaleService, PhoneNumberFormat } from '@lib/translation';

/**
 * Pipe for formatting phone numbers using libphonenumber-js.
 *
 * @example
 * {{ phoneNumber | fsPhoneNumber }}                         // Formats based on user's locale
 * {{ phoneNumber | fsPhoneNumber:'national' }}              // Override format
 * {{ phoneNumber | fsPhoneNumber:'international':'GB' }}    // Override both format and country
 */
@Pipe({
  name: 'fsPhoneNumber',
  pure: true,
})
export class FsPhoneNumberPipe implements PipeTransform {
  constructor(private readonly _localeService: LocaleService) {}

  /**
   * Transforms a phone number string into the specified format.
   *
   * @param value - The phone number string to format
   * @param format - The desired format: 'international', 'national', or 'e164' (optional, defaults to locale preference)
   * @param countryCode - The country code to use for parsing the phone number (optional, defaults to locale country)
   * @returns Formatted phone number string, or original value if parsing fails
   */
  transform(
    value: string | null | undefined,
    format?: PhoneNumberFormat,
    countryCode?: CountryCode,
  ): string {
    if (value == null || value === '' || value.trim() === '') {
      return '';
    }

    const trimmedValue = value.trim();
    if (!/\d/.test(trimmedValue)) {
      return '';
    }

    const defaultFormat = format ?? this._localeService.getPhoneDefaultFormat();
    const defaultCountryCode = countryCode ?? this._localeService.getPhoneDefaultCountryCode();

    try {
      const phoneNumber = trimmedValue.startsWith('+')
        ? parsePhoneNumber(trimmedValue)
        : parsePhoneNumber(trimmedValue, defaultCountryCode);

      if (!phoneNumber || !phoneNumber.isPossible()) {
        return trimmedValue;
      }

      switch (defaultFormat) {
        case 'international':
          return phoneNumber.formatInternational();
        case 'national':
          return phoneNumber.formatNational();
        case 'e164':
          return phoneNumber.format('E.164');
        default:
          return phoneNumber.formatNational();
      }
    } catch (error) {
      // If parsing fails, return the original value
      console.warn('FsPhoneNumberPipe: Failed to parse phone number:', value, error);
      return '';
    }
  }
}
